// assets/ck-koi-letter-hint.js
// Progressive, letter-anchored coach mark for the "tap a koi letter to select /
// double-tap to add to cart" gesture on group koi photos. The gesture is
// invisible (no affordance on touch; only a faint hover halo on desktop), so
// first-time visitors are taught it ONE step at a time, with the tip pinned
// right next to a real letter so "this"/"it" are literal:
//
//   Stage 1: "Tap a koi or its letter to select it" -> anchored at the first
//                                                     available letter
//   Stage 2: "Now double-tap it to add to cart"    -> anchored at the letter
//                                                     they just selected
//
// Persistence (localStorage `ck_koi_letter_hint`): '' = stage 1, 'select' =
// stage 2 (survives reloads), 'done' = finished. Advances only on a real
// (isTrusted) tap of an available letter or koi; finishes on close / cart
// add / 9s.
// The hint element survives variant-change re-renders; we reposition it on
// resize and as the selection changes so it stays glued to its letter.
(() => {
  if (window.__CK_KOI_LETTER_HINT__) return;
  window.__CK_KOI_LETTER_HINT__ = true;

  const KEY = 'ck_koi_letter_hint';
  const STAGE_MS = 9000;

  const read = () => { try { return localStorage.getItem(KEY) || ''; } catch (e) { return ''; } };
  const write = (v) => { try { localStorage.setItem(KEY, v); } catch (e) {} };
  const esc = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : s);
  const hints = () => document.querySelectorAll('[data-koi-letter-hint]');

  let timer = null;
  function arm() { if (timer) clearTimeout(timer); timer = setTimeout(finish, STAGE_MS); }
  function finish() {
    if (timer) { clearTimeout(timer); timer = null; }
    write('done');
    hints().forEach((h) => { h.hidden = true; });
  }

  // --- anchor targets ----------------------------------------------------
  function firstAvailable(ring) {
    return ring.querySelector('.koi-selected-ring__target:not([data-sold])');
  }
  function selectedLetter(ring) {
    const mark = ring.querySelector('.koi-selected-ring__mark.is-selected');
    return mark ? mark.dataset.letter : null;
  }
  // Stage 1 should point at an available koi that ISN'T already selected (so
  // tapping it visibly moves the selection — the first available one is normally
  // the one already selected on load), and prefer one near the MIDDLE of the
  // photo rather than a letter stuck up at the edge. Each target carries its
  // normalized position as inline-style percentages (set in koi-selected-ring),
  // so we can rank by distance from center (0.5, 0.5) without measuring layout.
  // Falls back to the first available when only one koi is available.
  function availableUnselected(ring) {
    const sel = selectedLetter(ring);
    const candidates = [].filter.call(
      ring.querySelectorAll('.koi-selected-ring__target:not([data-sold])'),
      (t) => t.dataset.letter !== sel
    );
    if (!candidates.length) return firstAvailable(ring);

    let best = candidates[0], bestDist = Infinity;
    for (const t of candidates) {
      const x = parseFloat(t.style.left) / 100;
      const y = parseFloat(t.style.top) / 100;
      if (isNaN(x) || isNaN(y)) continue;
      const dist = (x - 0.5) * (x - 0.5) + (y - 0.5) * (y - 0.5);
      if (dist < bestDist) { bestDist = dist; best = t; }
    }
    return best;
  }
  function selectedAvailable(ring) {
    const sel = selectedLetter(ring);
    if (sel) {
      const t = ring.querySelector('.koi-selected-ring__target[data-letter="' + esc(sel) + '"]:not([data-sold])');
      if (t) return t;
    }
    return firstAvailable(ring);
  }

  // The double-tap toggles, so stage 2 names the actual action for the anchored
  // koi: "add to cart" normally, "remove from cart" if it's already in the cart.
  // The ring's per-letter cart badges (kept current by main-product.liquid) are
  // the source of truth.
  function inCart(ring, letter) {
    if (!ring || !letter) return false;
    const badge = ring.querySelector('.koi-selected-ring__cart[data-letter="' + esc(letter) + '"]');
    return !!(badge && badge.classList.contains('is-in-cart'));
  }
  function updateAction(hint) {
    const action = hint.querySelector('[data-koi-hint-action]');
    if (!action) return;
    action.textContent = inCart(hint.closest('.koi-selected-ring'), hint.dataset.anchorLetter)
      ? 'remove from cart'
      : 'add to cart';
  }

  // --- positioning: pin the pill next to its anchor letter, edge-aware ----
  function place(hint) {
    if (!hint || hint.hidden) return;
    const ring = hint.closest('.koi-selected-ring');
    if (!ring) return;

    const letter = hint.dataset.anchorLetter;
    let target = letter ? ring.querySelector('.koi-selected-ring__target[data-letter="' + esc(letter) + '"]') : null;
    if (!target) target = selectedAvailable(ring);
    if (!target) return;

    const ringRect = ring.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    if (!ringRect.width || !ringRect.height) return; // not laid out yet — keep hidden

    const cx = (tRect.left + tRect.width / 2) - ringRect.left;
    const cy = (tRect.top + tRect.height / 2) - ringRect.top;
    const halfH = tRect.height / 2;
    const pad = 8, gap = 10;
    const pw = hint.offsetWidth, ph = hint.offsetHeight;

    // Prefer above the letter; flip below if there isn't room.
    let below = false;
    let top = cy - halfH - gap - ph;
    if (top < pad) { top = cy + halfH + gap; below = true; }

    // Center horizontally on the letter, clamped inside the image.
    let left = Math.max(pad, Math.min(cx - pw / 2, ringRect.width - pw - pad));

    hint.style.left = Math.round(left) + 'px';
    hint.style.top = Math.round(top) + 'px';
    hint.classList.toggle('koi-letter-hint--below', below);

    // Arrow keeps pointing at the letter even when the pill is clamped.
    const arrow = hint.querySelector('.koi-letter-hint__arrow');
    if (arrow) {
      const ax = Math.max(12, Math.min(cx - left, pw - 12));
      arrow.style.left = Math.round(ax - 5) + 'px';
    }
    hint.style.visibility = '';
  }
  function placeAll() { hints().forEach(place); }

  // --- stages ------------------------------------------------------------
  function showStage(stage, anchorTarget) {
    hints().forEach((h) => {
      h.querySelectorAll('[data-koi-hint-stage]').forEach((s) => {
        s.hidden = s.getAttribute('data-koi-hint-stage') !== String(stage);
      });
      h.hidden = false;
      h.style.visibility = 'hidden'; // until positioned, to avoid a corner flash

      const ring = h.closest('.koi-selected-ring');
      let target = (anchorTarget && ring && ring.contains(anchorTarget))
        ? anchorTarget
        : (ring ? (stage === 2 ? selectedAvailable(ring) : availableUnselected(ring)) : null);
      if (target) h.dataset.anchorLetter = target.dataset.letter;
      updateAction(h);
      place(h);
    });
    requestAnimationFrame(placeAll); // settle after measure/layout
  }

  function init() {
    if (!hints().length) return;
    const state = read();
    if (state === 'done') { hints().forEach((h) => { h.hidden = true; }); return; }

    // Mouse vs touch wording (capability, not screen size).
    const isPointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (isPointer) {
      document.querySelectorAll('[data-koi-hint-verb]').forEach((el) => { el.textContent = 'Click'; });
      document.querySelectorAll('[data-koi-hint-verb2]').forEach((el) => { el.textContent = 'double-click'; });
    }

    showStage(state === 'select' ? 2 : 1);
    arm();

    document.querySelectorAll('[data-koi-letter-hint] .koi-letter-hint__close').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); finish(); });
      // span[role=button] needs manual Enter/Space activation.
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); finish(); }
      });
    });

    // Stage 1 -> 2 on the first real tap of an available letter or koi; while
    // in stage 2, follow the newly selected koi so "it" keeps pointing at its
    // letter, where the double-tap adds it.
    document.addEventListener('pointerdown', (e) => {
      if (!e.isTrusted) return;
      const target = e.target.closest && e.target.closest('.koi-selected-ring__target');
      if (!target || target.dataset.sold) return;
      advance(target);
    }, true);
    // A tap on the koi itself (main-product.liquid sends this only for a real
    // tap that selected it).
    document.addEventListener('ck:koi-area-select', (e) => {
      const ring = e.detail && e.detail.ring;
      const letter = e.detail && e.detail.letter;
      if (!ring || !letter) return;
      const target = ring.querySelector('.koi-selected-ring__target[data-letter="' + esc(letter) + '"]:not([data-sold])');
      if (target) advance(target);
    });
    function advance(target) {
      const state2 = read();
      if (state2 === '') {
        write('select');
        showStage(2, target);
        arm();
      } else if (state2 === 'select') {
        hints().forEach((h) => {
          const ring = h.closest('.koi-selected-ring');
          if (ring && ring.contains(target)) { h.dataset.anchorLetter = target.dataset.letter; updateAction(h); place(h); }
        });
      }
    }

    // Once they actually add/remove, they've got it.
    document.addEventListener('cart:refresh', () => { if (read() === 'select') finish(); });

    // Keep the pill glued to its letter as the image resizes / reflows.
    window.addEventListener('resize', placeAll);
    window.addEventListener('load', placeAll);
    const img = document.querySelector('media-gallery, [id^="MediaGallery-"]');
    if (img && window.ResizeObserver) { try { new ResizeObserver(placeAll).observe(img); } catch (e) {} }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
